<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {useAuthStore} from "~/plugins/store/auth";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import {
  createDefaultUserChangePassword, createDefaultUserChangePasswordValidation,
  type UserChangePassword, type UserChangePasswordValidation,
} from "~/types/Customer";
import type {TranslationList} from "~/types/Utils";
import {recaptcha} from "~/composables/recaptcha";
import {useNuxtApp} from "#app";
import {useUserStore} from "~/plugins/store/user";
import {useReCaptcha} from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import ConfirmationDialog from "~/components/utils/ConfirmationDialog.vue";
import {reloadNuxtApp} from "nuxt/app";
import {useSystemStore} from "~/plugins/store/system";
import {useTwoFactorStore} from "~/plugins/store/two-factor";

export default defineComponent({
  components: {ConfirmationDialog, AccountLayout, LoadingDialog},
  setup() {
    definePageMeta({
      middleware: [
        'auth',
      ],
    });

    useHead({
      title: 'Xiga | Securitate',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });
    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const userChangePasswordForm = ref<UserChangePassword>(createDefaultUserChangePassword());
    const validationUserChangePasswordStatus = ref<UserChangePasswordValidation>(createDefaultUserChangePasswordValidation());
    const {$toast} = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');
    const showConfirmationDialog = ref(false);
    const messageConfirmationDialog = ref('');
    const messageContextDialog = ref('');
    const yesButtonText = ref('Da');
    const noButtonText = ref('Nu');

    const systemStore = useSystemStore();
    const {userAgent} = useDevice();
    const twoFactorStore = useTwoFactorStore();
    const isTwoFactorEnabledForUser = ref(false);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = 'Se încarcă...';
      if (accessToken) {
        const response = await twoFactorStore.checkActiveSession(accessToken, userAgent, <string>systemStore.clientIp);
        if (response) {
          isTwoFactorEnabledForUser.value = response.is_two_factor_enabled_for_user;
        }
      }
      showLoadingDialog.value = false;
    });

    const handleValidationOldPassword = (status: boolean) => {
      validationUserChangePasswordStatus.value.old_password = status;
    };

    const handleValidationNewPassword = (status: boolean) => {
      validationUserChangePasswordStatus.value.new_password = status;
    };

    const handleValidationNewPasswordConfirmation = (status: boolean) => {
      validationUserChangePasswordStatus.value.new_password_confirmation = status;
    };

    const triggerConfirmationDialog = () => {
      showConfirmationDialog.value = true;
      messageConfirmationDialog.value = translation(translationList.value.confirm_deactivate_account, 'Ești sigur că vrei să îți dezactivezi contul?');
      messageContextDialog.value = translation(translationList.value.confirm_deactivate_account_context, 'Dacă îți dezactivezi contul, vei fi deconectat și nu vei mai putea accesa contul tău fără a reseta parola.');
    };

    const handleConfirmationDialogResponse = async ({response, identifier}: {
      response: boolean,
      identifier: string
    }) => {
      if (response && user.value && accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = translation(translationList.value.customer_details_validating, 'Validăm datele introduse...');
        // Introduce a delay of 1.5 second (1500 milliseconds)
        await delay(1500);
        const token = await recaptcha(recaptchaInstance);
        userStore.deactivateUser(user.value.id, accessToken, token).then(async (response) => {
          if (response) {
            $toast.success(translation(translationList.value.customer_details_saved, 'Contul a fost dezactivat cu succes!'));
            await authStore.logout();
            reloadNuxtApp();
          } else {
            $toast.error(translation(translationList.value.customer_details_not_saved, 'Contul nu a fost dezactivat!'));
          }
        }).catch((error) => {
          $toast.error(translation(translationList.value.customer_details_not_saved, 'Contul nu a fost dezactivat!'));
        }).finally(() => {
          showLoadingDialog.value = false;
        });
      } else {
        showConfirmationDialog.value = false;
      }
    };

    const handleValidation = (field: string, status: boolean) => {
      if (!status) {
        console.log(`Validation failed for field: ${field}`);
        window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
        return false;
      }
      return true;
    };

    const submitChangePassword = async () => {
      let isValid = true;
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.customer_details_validating, 'Validăm datele introduse...');
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      for (const field in validationUserChangePasswordStatus.value) {
        if (!handleValidation(field, validationUserChangePasswordStatus.value[field])) {
          isValid = false;
        }
      }
      if (!isValid || !user.value || !accessToken) {
        showLoadingDialog.value = false;
        $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
        return;
      }

      if (userChangePasswordForm.value.new_password !== userChangePasswordForm.value.new_password_confirmation) {
        showLoadingDialog.value = false;
        $toast.error(translation(translationList.value.customer_details_not_saved, 'Parola nouă și confirmarea parolei nu se potrivesc!'));
        return;
      }

      messageLoadingDialog.value = translation(translationList.value.customer_details_saving, 'Actualizăm parola...');
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      const token = await recaptcha(recaptchaInstance);
      userStore.changePassword(userChangePasswordForm.value, user.value.id, accessToken, token).then((response) => {
        if (response) {
          $toast.success(translation(translationList.value.customer_details_saved, 'Parola a fost actualizată cu succes!'));
        } else {
          $toast.error(translation(translationList.value.customer_details_not_saved, 'Parola nu a fost actualizată!'));
        }
      }).catch((error) => {
        $toast.error(translation(translationList.value.customer_details_not_saved, 'Parola nu a fost actualizată!'));
      }).finally(() => {
        showLoadingDialog.value = false;
      });
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      handleValidationOldPassword,
      handleValidationNewPassword,
      handleValidationNewPasswordConfirmation,
      userChangePasswordForm,
      submitChangePassword,
      showLoadingDialog,
      messageLoadingDialog,
      showConfirmationDialog,
      messageContextDialog,
      messageConfirmationDialog,
      handleConfirmationDialogResponse,
      triggerConfirmationDialog,
      yesButtonText,
      noButtonText,
      isTwoFactorEnabledForUser
    }
  }
});
</script>

<template>
  <account-layout class="profile">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
      <div>
        <v-row>
          <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
            <h3 class="font-weight-bold">
              {{ translation(translationList.change_password, 'Schimbă parola contului tău') }}
            </h3>
            <p class="font-weight-regular">
              {{
                translation(translationList.change_password_helper, 'Parola trebuie să conțină cel puțin 8 caractere, o literă mare, o literă mică și un număr.')
              }}
            </p>
          </v-col>
        </v-row>
        <div v-if="!showLoadingDialog">
          <v-row class="d-flex align-center">
            <v-col cols="12" sm="12" md="12">
              <customer-password v-model="userChangePasswordForm.old_password"
                                 :label="translation(translationList.old_password, 'Parolă curentă')"
                                 @validation-customer-status="handleValidationOldPassword"></customer-password>
            </v-col>
          </v-row>

          <v-row class="d-flex align-center">
            <v-col cols="12" sm="12" md="6">
              <customer-password v-model="userChangePasswordForm.new_password"
                                 :label="translation(translationList.new_password, 'Parolă nouă')"
                                 @validation-customer-status="handleValidationNewPassword"></customer-password>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <customer-password v-model="userChangePasswordForm.new_password_confirmation"
                                 :label="translation(translationList.new_password_confirmation, 'Confirmare parolă nouă')"
                                 @validation-customer-status="handleValidationNewPasswordConfirmation"></customer-password>
            </v-col>
          </v-row>

          <v-row class="mt-10 mb-15">
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-btn
                  class="block-on-mobile"
                  color="primary"
                  size="x-large"
                  flat
                  rounded="lg"
                  @click="submitChangePassword"
              >{{ translation(translationList.change_password_button, 'Schimbă parola') }}
              </v-btn>
            </v-col>
          </v-row>

        </div>
        <v-skeleton-loader v-else type="image" class="skeleton-h-250"></v-skeleton-loader>
      </div>
      <div>
        <confirmation-dialog
            :show-dialog="showConfirmationDialog"
            :message="messageConfirmationDialog"
            @response="handleConfirmationDialogResponse"
            :context="messageContextDialog"
            :yes-button-text="yesButtonText"
            :no-button-text="noButtonText"
        ></confirmation-dialog>
          <v-row>
            <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
              <h3 class="font-weight-bold">
                {{ translation(translationList.my_profile, 'Dezactivează contul') }}
              </h3>
              <p class="font-weight-regular">
              <span
                  v-html="translation(translationList.my_profile_helper, 'Dacă dorești să îți dezactivezi contul, te rugăm apasă butonul de mai jos. <br>Contul tău va fi dezactivat, pentru a îl activa din nou, va fi necesar resetarea parolei.')"></span>

              </p>
            </v-col>
          </v-row>
        <div v-if="!showLoadingDialog">
          <v-row class="mt-10 mb-15">
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-btn
                  class="block-on-mobile"
                  color="error"
                  size="x-large"
                  variant="outlined"
                  rounded="lg"
                  @click="triggerConfirmationDialog"
              >{{ translation(translationList.save_changes, 'Dezactivează contul') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <v-skeleton-loader v-else type="image" class="skeleton-h-250"></v-skeleton-loader>
      </div>
    </v-card>
  </account-layout>
</template>

<style scoped>

</style>
